import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { HourlyPayLabel } from "@src/appV2/redesign/Pay/HourlyPayLabel";
import { TotalPayLabel } from "@src/appV2/redesign/Pay/TotalPayLabel";
import { getFormattedShiftPayBreakdown } from "@src/appV2/redesign/Shift/getFormattedShiftPayBreakdown";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

import { Button } from "../../../components/Button";
import { StickyFooter } from "../../../components/StickyFooter";
import { SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI } from "../constants";
import { useFooterPrimaryAction } from "../hooks/useShiftActionVisibility";
import { CancelShiftButton } from "./CancelShiftButton";

export interface NonInstantPayFooterProps {
  shift: WorkerShift;
  shiftState: ShiftStateData;
  currentDate: Date;
  onUpdateETA: () => void;
  onCancelShift: () => void;
  onSentHome: () => void;
  onUploadTimesheet: () => void;
  isUploadingTimesheet: boolean;
  isMissedPunchRequestEnabled: boolean;
}

function NonInstantPayFooter(props: NonInstantPayFooterProps) {
  const {
    shift,
    shiftState,
    currentDate,
    onUpdateETA,
    onCancelShift,
    onSentHome,
    onUploadTimesheet,
    isUploadingTimesheet,
    isMissedPunchRequestEnabled,
  } = props;

  const { primaryAction, showCancelShift } = useFooterPrimaryAction({
    shift,
    shiftState,
    currentDate,
  });

  const shiftLengthInHours = differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) / 60;

  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    shift.originalAmount,
    undefined,
    shiftLengthInHours,
    shift.facility.requiresLunchBreak
  );

  const renderPrimaryButton = () => {
    if (!primaryAction) {
      return null;
    }

    const { action } = primaryAction;

    // FIXME: Automatically disabled during ESLint config update
    switch (action) {
      case "uploadTimesheet": {
        return (
          <LoadingButton
            fullWidth
            isLoading={isUploadingTimesheet}
            size="large"
            variant="contained"
            onClick={() => {
              onUploadTimesheet();
            }}
          >
            {isMissedPunchRequestEnabled ? "Request shift payment" : "Upload Timesheet"}
          </LoadingButton>
        );
      }

      case "updateEta": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_LATE_FOR_SHIFT_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onUpdateETA();
            }}
          >
            Update ETA
          </Button>
        );
      }

      case "sentHome": {
        return (
          <Button
            fullWidth
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SENT_HOME_TAPPED, {
                shiftId: shift._id,
                sourceUI: SHIFT_DETAILS_FOOTER_LOG_EVENT_SOURCE_UI,
              });
              onSentHome();
            }}
          >
            I was sent home
          </Button>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <StickyFooter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        <HourlyPayLabel size="large" hourlyPay={payBreakdown?.hourlyPay} />
        <Stack direction="row" spacing={2} alignItems="center">
          <TotalPayLabel
            totalPay={payBreakdown?.workedBreakPay?.totalPay ?? payBreakdown?.totalPay}
          />
          {/* Non-instant pay shifts don't show instant pay indicator */}
        </Stack>
      </Stack>

      <Stack spacing={3}>
        {showCancelShift && <CancelShiftButton shiftId={shift._id} onCancelShift={onCancelShift} />}
        {renderPrimaryButton()}
      </Stack>
    </StickyFooter>
  );
}

export { NonInstantPayFooter };
